import { FormEvent, FC, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '@/lib';
import { BaseModal } from '@/lib/Modals';
import { ModalDescription, ModalTitle } from '@/lib/Modals/common';
import { useWhatsappTemplates } from '@/features/campaigns/v2/hooks/useWhatsappTemplates';
import { countPositionalPlaceholders } from '@/features/campaigns/components/CreateCampaign_v2/steps/Whatsapp/templateVariableExamples';
import { useWorkspace } from '@/features/workspaces/hooks';
import { useSendWhatsappMessageMutation, type WhatsappConversation } from '@/services/endpoints/whatsappInbox';
import { contactLabel, fillTemplate } from '../inboxUtils';

type Props = {
  conversation: WhatsappConversation;
  open: boolean;
  onClose: VoidFunction;
  onSentNotRecorded?: (message: string) => void;
  onSendFailed?: (message: string) => void;
  onSendSuccess?: VoidFunction;
  /** When true, hide marketing templates (contact opted out of marketing). */
  excludeMarketing?: boolean;
  reportIfUnmounted?: (error: unknown) => boolean;
};

const errorMessage = (error: unknown): string | undefined => (error as { data?: { message?: string } } | undefined)?.data?.message;
const isSentNotRecorded = (error: unknown): boolean => errorMessage(error) === 'i18n_WHATSAPP_SENT_NOT_RECORDED';
const isMarketingTemplate = (category?: string | null) => !category || category === 'MARKETING';

export const SendTemplateModal: FC<Props> = ({
  conversation,
  open,
  onClose,
  onSentNotRecorded,
  onSendFailed,
  onSendSuccess,
  excludeMarketing = false,
  reportIfUnmounted,
}) => {
  const { t } = useTranslation('whatsappInbox');
  const workspace = useWorkspace();
  const { data, isLoading } = useWhatsappTemplates();
  const approved = (data ?? [])
    .flatMap((account) => account.templates)
    .filter((template) => template.status === 'APPROVED' && (!excludeMarketing || !isMarketingTemplate(template.category)));
  const [templateId, setTemplateId] = useState('');
  const [params, setParams] = useState<string[]>([]);
  const [problem, setProblem] = useState<string | null>(null);
  const [sendMessage, { isLoading: isSending }] = useSendWhatsappMessageMutation();
  const template = approved.find((candidate) => candidate.id === templateId);
  const ready = !!template && params.every((param) => param.trim());

  const choose = (id: string) => {
    const chosen = approved.find((candidate) => candidate.id === id);
    setTemplateId(id);
    setParams(Array(chosen ? (chosen.variables?.length ?? countPositionalPlaceholders(chosen.body ?? '')) : 0).fill(''));
    setProblem(null);
  };

  const submit = async (event: FormEvent) => {
    event.preventDefault();
    if (!ready || isSending) return;
    try {
      await sendMessage({
        workspaceId: workspace.id,
        conversationId: conversation.id,
        body: { type: 'template', templateId, params },
      }).unwrap();
      choose('');
      onSendSuccess?.();
      onClose();
    } catch (error) {
      if (reportIfUnmounted?.(error)) {
        onClose();
        return;
      }
      if (isSentNotRecorded(error)) {
        choose('');
        onSentNotRecorded?.(t('sentNotRecorded'));
        onClose();
      } else {
        const message = t(errorMessage(error) === 'i18n_WHATSAPP_WINDOW_CLOSED' ? 'windowClosedTitle' : 'sendFailed');
        setProblem(message);
        onSendFailed?.(message);
      }
    }
  };

  if (!open) return null;

  return (
    <BaseModal
      onClose={onClose}
      aria-labelledby='send-template-title'
      classNames={{ dialog: 'max-w-3xl w-[min(900px,100%)]', content: 'min-w-0 min-h-0 gap-5 bg-white-100 p-6' }}
    >
      <div>
        <ModalTitle className='text-xl font-bold'>
          <span id='send-template-title'>{t('templateModalTitle')}</span>
        </ModalTitle>
        <ModalDescription>{t('templateModalSubtitle', { contact: contactLabel(conversation) })}</ModalDescription>
      </div>
      <form className='grid gap-5 md:grid-cols-[minmax(0,1fr)_minmax(12rem,16rem)]' onSubmit={submit}>
        <div className='flex min-w-0 flex-col gap-4'>
          <label className='flex flex-col gap-1.5 text-[13px] font-semibold text-primary-900'>
            {t('templatePlaceholder')}
            <select
              value={templateId}
              disabled={isLoading}
              className='rounded-lg border border-primary-border bg-white-100 px-2.5 py-2 text-[13px] font-normal'
              onChange={(event) => choose(event.target.value)}
            >
              <option value=''>{t('templatePlaceholder')}</option>
              {approved.map((candidate) => (
                <option key={candidate.id} value={candidate.id}>
                  {candidate.name}
                </option>
              ))}
            </select>
          </label>
          {template && params.length > 0 ? (
            <div className='rounded-xl border border-primary-border p-3.5'>
              <p className='mb-1 text-[13px] font-semibold'>{t('variableMappingTitle')}</p>
              <p className='mb-3 text-xs text-primary-900/60'>{t('variableMappingHint')}</p>
              <div className='flex flex-col gap-2.5'>
                {params.map((value, index) => (
                  <div key={index} className='grid grid-cols-[3.25rem_1fr] items-center gap-2.5'>
                    <code className='rounded-md bg-primary-background px-1.5 py-1 text-center text-xs text-primary-900/80'>{`{{${index + 1}}}`}</code>
                    <input
                      value={value}
                      aria-label={t('templateParam', { index: index + 1 })}
                      placeholder={t('templateParam', { index: index + 1 })}
                      className='rounded-lg border border-primary-border px-2.5 py-2 text-[13px]'
                      onChange={(event) => setParams(params.map((old, i) => (i === index ? event.target.value : old)))}
                    />
                  </div>
                ))}
              </div>
            </div>
          ) : null}
          {problem ? (
            <p role='alert' className='text-sm text-red-600'>
              {problem}
            </p>
          ) : null}
        </div>
        <div className='min-w-0'>
          <div className='mb-2 flex items-center justify-between gap-2'>
            <span className='text-[13px] font-semibold'>{t('whatsappPreview')}</span>
            <span className='rounded-full bg-green-100 px-2 py-0.5 text-[11px] font-semibold text-green-700'>{t('templateBadge')}</span>
          </div>
          <div className='flex min-h-[220px] flex-col rounded-2xl border-8 border-primary-900 bg-primary-background p-3'>
            {template?.body ? (
              <p className='ml-auto w-fit max-w-[92%] whitespace-pre-wrap break-words rounded-[10px] rounded-tr-sm bg-brand-600 px-2.5 py-1.5 text-xs leading-snug text-white-100'>
                {fillTemplate(template.body, params)}
              </p>
            ) : (
              <p className='m-auto text-center text-xs text-primary-900/50'>{t('templatePreviewEmpty')}</p>
            )}
          </div>
        </div>
        <div className='flex justify-end gap-2.5 md:col-span-2'>
          <Button type='button' intent='secondary' onClick={onClose}>
            {t('cancel')}
          </Button>
          <Button type='submit' disabled={!ready} isLoading={isSending}>
            {t('send')}
          </Button>
        </div>
      </form>
    </BaseModal>
  );
};
